import { useState, useEffect } from 'react';
import axios from 'axios';
import { Routing } from '../Routes';
import { useApi, Res } from '../../state/api';
import { Main } from '../Main/Main';
import { useTranslation } from '../../i18n/i18n';
import { LoaderPage } from './LoaderPage';
import { ErrorPage } from './ErrorPage';
import { ApiActionResponse } from '../../types';
import { useConfig } from '../../config';
import { Button } from '../Form/Button';
import { ContentCard } from '../ContentCard/ContentCard';
import { InputGrid } from '../Form/InputGrid';
import { Loader } from '../Loader/Loader';
import './CommunicationPage.css';

export const CommunicationPage = (): JSX.Element => {
	const t = useTranslation().communication;
	const generic = useTranslation().generic;
	const { api } = useConfig();
	const { data: communications, error } = useApi(Res.communications, {});
	const { data: subIds, isLoading, mutate } = useApi(Res.subscriptions, {});
	const [checked, setChecked] = useState<number[]>([]);
	const [buttonText, setButtonText] = useState(t.saveChanges);
	const [message, setMessage] = useState('');
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (subIds) setChecked(subIds);
	}, [isLoading, subIds]);

	async function post(payload: number[]): Promise<{ data?: ApiActionResponse; error?: unknown }> {
		return axios.post<ApiActionResponse>(api + Res.subscriptions.uri({}), payload, { withCredentials: true });
	}

	if (error) return <ErrorPage />;
	if (!communications || !subIds) return <LoaderPage />;

	const handleOnChange = (id: number) => {
		// whatever the last message said - saved, or failed - a new selection makes it untrue
		setMessage('');

		if (checked.includes(id)) {
			setChecked(checked.filter((i) => i !== id));
		} else {
			setChecked([...checked, id]);
		}
	};
	const noChanges = subIds.length === checked.length && checked.every((item) => subIds.indexOf(item) > -1);

	async function handleSubmit() {
		const payload: number[] = checked;

		setLoading(true);
		setMessage('');

		/*
		 * Nothing optimistic here. The effect above copies the cached list over `checked` whenever
		 * it changes, so writing the attempt into the cache and then putting the cache back on a
		 * failure erases the selection the error message is asking them to try again - and leaving
		 * the attempt in the cache instead makes `noChanges` true, which disables the button. Not
		 * touching it until the write has gone through leaves the selection where the customer put
		 * it, and different from the server, which is what keeps the button alive.
		 */
		try {
			const res = await post(payload);

			if (res.data !== undefined) {
				setButtonText(t.saved);
				setMessage(t.changesSaved);
				mutate();
			}
		} catch (e) {
			setMessage(generic.somethingWentWrong);
		} finally {
			// the button is pressed and forgotten, so a failure used to leave it loading for good
			setLoading(false);
		}
	}

	return (
		<Main>
			<div id="communication-page">
				<ContentCard title={t.subscriptionSettings}>
					<p>{t.handleCommunication}</p>
					<InputGrid>
						{communications.map((item) => (
							<label key={item.id} className="communication-item">
								<input
									type="checkbox"
									className="communication-item-checkbox"
									value={item.id}
									checked={checked.includes(item.id)}
									onChange={() => handleOnChange(item.id)}
									disabled={loading}
								/>
								<span className="communication-item-text">
									<strong>{item.name}</strong> - {item.description}
								</span>
							</label>
						))}
					</InputGrid>
					<p className="important-info">
						<span>{t.importantInformation}</span>
					</p>
					<div className="card-footer">
						{message}
						{loading && <Loader size="icon" />}
						<Button disabled={noChanges || loading} onClick={handleSubmit}>
							{noChanges ? buttonText : t.saveChanges}
						</Button>
					</div>
				</ContentCard>
			</div>
		</Main>
	);
};

export const communicationRouting: Routing = {
	path(urls) {
		return urls.communication;
	},
	component: CommunicationPage,
	requiresAuth: true,
};
